/**
 * AcquiringPrerequisiteNote — 框架通道「收单前置」提示块（ADM-PAY-S008 切换收单方确认页 / 编辑策略组的框架通道 tab）。
 *
 * 目标通道是框架通道（如 PayerMax）时，按本 App 在该通道的配置（A8 §10.1，viewer+）判 C 端能否下发：未就绪 → warn
 * 提示「C 端不会下发」，但**不阻断**（契约写侧不做 serving 校验）；读取失败 → 「无法判定」（不把未知当满足）；
 * 后台读得到的条件都满足 → 中性说明，写明回跳 host 登记与环境放行由 C 端判定，不宣称一定会下发。
 * 判据见 `~/data/adapters/acquiring-prerequisite`。非框架通道不渲染。除读取中外每一态都给「去 {通道} 配置」链接
 * （`paymentProviderConfigRoute`），与支付页渠道卡、运营弹窗落点的提示同一入口。
 */

import { type MouseEvent, type ReactNode, createElement } from 'react';
import { Link } from 'react-router-dom';
import {
  acquiringPrerequisiteOf,
  describeAcquiringPrerequisite,
  isPaymentProviderChannel,
} from '~/data/adapters/acquiring-prerequisite';
import {
  paymentProviderConfigRoute,
  usePaymentProviderConfigs,
} from '~/data/payment-provider-config-queries';
import { Icons } from '~/lib/icons';
import { channelDisplayLabel } from './payment-method-labels';

/** 提示块：info = 说明，warn = 未满足 / 无法判定（色值全取 token，同 §3.1 提示块体例） */
function PrerequisiteBlock({
  tone,
  icon,
  children,
}: {
  tone: 'info' | 'warn';
  icon: keyof typeof Icons;
  children: ReactNode;
}) {
  return (
    <div
      className="hstack"
      style={{
        gap: 8,
        alignItems: 'flex-start',
        padding: '10px 12px',
        borderRadius: 7,
        background: `var(--${tone}-soft)`,
        color: tone === 'info' ? 'var(--fg)' : `var(--${tone})`,
        fontSize: 11.5,
        lineHeight: 1.6,
      }}
    >
      <span style={{ flexShrink: 0, marginTop: 2 }}>
        {createElement(Icons[icon], { size: 12 })}
      </span>
      <div>{children}</div>
    </div>
  );
}

export function AcquiringPrerequisiteNote({
  appId,
  channel,
  isNativeApp,
  actionLabel,
  onConfigLinkClick,
}: {
  appId: string;
  /** 结算通道值；不是框架通道时不渲染 */
  channel: string;
  /** 原生 App 另判 `native_return_base_url`（字典 65 §1）；`null` = 平台未知（不判该项，指引里条件式提醒） */
  isNativeApp: boolean | null;
  /** 当前动作名（「切换」/「保存」），用于写明「{动作}本身不受影响」 */
  actionLabel: string;
  /**
   * 点「去 {通道} 配置」时的宿主守卫：宿主有未保存草稿 / 提交在途时调 `event.preventDefault()` 拦下跳转，
   * 自行确认后再导航（链接本身离开当前页，弹窗随页面卸载）。缺省 = 直接跳转。
   */
  onConfigLinkClick?: (to: string, event: MouseEvent<HTMLAnchorElement>) => void;
}) {
  const providerChannel = isPaymentProviderChannel(channel) ? channel : null;
  const configs = usePaymentProviderConfigs(providerChannel);
  if (providerChannel === null) return null;
  const channelName = channelDisplayLabel(channel);
  const configLink = (
    <div style={{ marginTop: 4 }}>
      <Link
        to={paymentProviderConfigRoute(providerChannel)}
        onClick={(event) => onConfigLinkClick?.(paymentProviderConfigRoute(providerChannel), event)}
      >
        去 {channelName} 配置
      </Link>
    </div>
  );
  // Web App 的回跳地址恒为 NULL（字典 65 §1），指引只对原生 App 提它
  const handling = `请联系 root / admin 在 ${channelName} 配置中启用并完成连接测试${
    isNativeApp === true
      ? '，填好原生回跳地址'
      : isNativeApp === null
        ? '（原生 App 另须填好原生回跳地址）'
        : ''
  }。`;
  if (configs.isLoading) {
    return (
      <PrerequisiteBlock tone="info" icon="IconInfo">
        正在读取 {channelName} 配置，判定收单前置…
      </PrerequisiteBlock>
    );
  }
  if (configs.isError || configs.data === undefined) {
    return (
      <PrerequisiteBlock tone="warn" icon="IconAlert">
        {channelName} 收单前置：<strong>无法判定</strong>（配置读取失败或离线）。{actionLabel}
        本身不受影响，但{channelName} 配置未就绪时 C 端不会下发 {channelName} 支付方式。{handling}
        {configLink}
      </PrerequisiteBlock>
    );
  }
  const prerequisite = acquiringPrerequisiteOf(configs.data, appId, {
    isNativeApp: isNativeApp === true,
  });
  const problem = describeAcquiringPrerequisite(prerequisite);
  if (problem === null) {
    // 平台未知时没判回跳地址：配置里确实缺它就条件式提醒，免得原生 App 被当成已就绪
    const nativeReturnUnchecked =
      isNativeApp === null &&
      configs.data.find((row) => row.app_id === appId)?.native_return_base_url === null;
    return (
      <PrerequisiteBlock
        tone={nativeReturnUnchecked ? 'warn' : 'info'}
        icon={nativeReturnUnchecked ? 'IconAlert' : 'IconInfo'}
      >
        {channelName} 收单前置：本 App 配置已启用且已连接。
        {nativeReturnUnchecked &&
          '本 App 平台未知、配置里没有原生回跳地址：如为原生 App，C 端不会下发，须先填好。'}
        回跳 host 登记与部署环境放行由 C 端判定，后台读不到；如 C 端仍未展示，请到配置页复核。
        {configLink}
      </PrerequisiteBlock>
    );
  }
  return (
    <PrerequisiteBlock tone="warn" icon="IconAlert">
      <strong>收单前置未满足</strong>：本 App 的 {channelName} 配置<strong>{problem}</strong>。C
      端不会下发 {channelName} 支付方式，该策略组覆盖的用户将看不到这些支付方式。{actionLabel}
      本身不受影响。{handling}
      {configLink}
    </PrerequisiteBlock>
  );
}
